import React from "react";
import { Award } from "lucide-react";
import ComboIcon from "./ComboIcon";

export const IngredientTag = ({ combo, className = "" }) => {
    if (!combo) return null;
    return (
        <span className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] ${className}`}
            style={{ color: combo.color || "#E5A900" }}>
            <ComboIcon icon={combo.icon} url={combo.icon_url} color={combo.color} className="w-3.5 h-3.5" strokeWidth={3} />
            {combo.name?.replace(/ combo$/i, "")}
        </span>
    );
};

export const PremiumBadge = ({ label = "Patented Premium Active" }) => (
    <span
        data-testid="premium-badge"
        className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] font-bold text-[#111] px-2.5 py-1 rounded-full"
        style={{ background: "linear-gradient(100deg,#F7E7A6 0%,#E5A900 45%,#B8860B 100%)", boxShadow: "0 4px 14px rgba(229,169,0,0.28)" }}
    >
        <Award className="w-3 h-3" /> {label}
    </span>
);

export const IngredientCard = ({ ingredient, combo, index, hero = false, showDescription = true, testId }) => {
    const premium = !!ingredient.premium;
    return (
        <div
            data-testid={testId}
            className={`group relative p-6 rounded-sm border transition-colors overflow-hidden h-full ${
                premium
                    ? "border-gold/45 bg-gradient-to-br from-gold/[0.14] via-gold/[0.04] to-transparent hover:border-gold/70"
                    : hero
                        ? "bg-gradient-to-br from-lime/15 to-transparent border-lime/30"
                        : "bg-bg0 border-white/10 hover:bg-white/5 hover:border-lime/30"
            }`}
        >
            {premium && (
                <div className="pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(229,169,0,0.22) 0%, rgba(0,0,0,0) 70%)" }} />
            )}

            <div className="relative flex items-start justify-between gap-3">
                <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">
                    Active {String(index + 1).padStart(2, "0")}
                </div>
                <IngredientTag combo={combo} />
            </div>

            {premium && (
                <div className="relative mt-4">
                    <PremiumBadge label={ingredient.badge_label || "Patented Premium Active"} />
                </div>
            )}

            <h4 className={`relative mt-2 font-display tracking-tight ${premium || hero ? "text-3xl" : "text-lg"}`}>
                {ingredient.name}
            </h4>

            <div className="relative mt-2 flex items-baseline gap-2">
                <span className={`font-display text-gold ${premium || hero ? "text-3xl" : "text-xl"}`}>
                    {ingredient.quantity} {ingredient.unit}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">per tablet</span>
            </div>

            {ingredient.botanical_name && (
                <div className="relative mt-1.5 text-xs italic text-zinc-400">{ingredient.botanical_name}</div>
            )}
            {showDescription && ingredient.description && (
                <p className={`relative mt-3 text-zinc-400 ${premium || hero ? "text-base max-w-sm" : "text-sm"}`}>
                    {ingredient.description}
                </p>
            )}
        </div>
    );
};

export default IngredientCard;
